import React, { memo, useMemo } from 'react'

import { Button } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  arrayOf, bool, func, number, string,
} from 'prop-types'

import formatResultsCount from 'Pods/SearchPage/Shared/formatResultsCount'
import { FILTER_NAME } from 'Pods/SearchPage/Shared/Tracking/constants'
import { useTrackFilterSelect } from 'Pods/SearchPage/Shared/Tracking/FilterTracking'
import useSuggestedTags from 'Pods/SearchPage/Shared/useSuggestedTags'
import useTranslate, { useLocale } from 'Pods/SearchPage/Shared/useTranslate'

import TermChip from './Components/TermChip/TermChip'

// Selected terms stay in here rather than in the filter row (accepted design, CU-124g39pbfna).
// The parent owns the terms: live store values from lg, a staged copy below it until Search.
function Panel({
  draft,
  isStaged = false,
  resultCount = null,
  terms,
  tags,
  isResetDisabled,
  onAddDraft,
  onAddTag,
  onRemoveTag,
  onRemoveTerm,
  onReset,
  onSearch,
  onTermChange,
}) {
  const translate = useTranslate()
  const locale = useLocale()
  const trackFilterSelect = useTrackFilterSelect()

  const suggestedTags = useSuggestedTags()
  // null while the count for the staged terms is loading; "..." keeps the button from
  // promising the count of the results already on the page.
  const formattedTotal = resultCount === null ? '...' : formatResultsCount(resultCount, locale)

  const relatedTags = useMemo(() => {
    const selectedSet = new Set(tags)
    return suggestedTags.filter(tag => !selectedSet.has(tag))
  }, [tags, suggestedTags])

  // A staged tag is reported by the parent when Search applies it.
  const handleAdd = (tag) => {
    if (!isStaged) trackFilterSelect(FILTER_NAME.TAGS, tag)
    onAddTag(tag)
    onTermChange()
  }

  const handleRemove = (tag) => {
    onRemoveTag(tag)
    onTermChange()
  }

  const handleRemoveTerm = (term) => {
    onRemoveTerm(term)
    onTermChange()
  }

  const draftTerm = draft.trim()
  const showDraft = draftTerm !== '' && !terms.includes(draftTerm)

  return (
    <div className="search-within__content">
      {(terms.length > 0 || tags.length > 0) && (
        <div
          className="search-within__selected"
          role="group"
          aria-label={translate('search_page.filters.search_within.selected_group_label')}
        >
          {terms.map(term => (
            <TermChip
              key={`term-${term}`}
              label={term}
              removeLabel={translate('search_page.filters.search_within.remove_term', { term })}
              onRemove={handleRemoveTerm}
            />
          ))}
          {tags.map(tag => (
            <TermChip
              key={`tag-${tag}`}
              label={tag}
              removeLabel={translate('search_page.filters.search_within.remove_term', { term: tag })}
              onRemove={handleRemove}
            />
          ))}
        </div>
      )}
      {(showDraft || relatedTags.length > 0) && (
        <div className="search-within__related">
          <span id="search-within-related-title" className="search-within__related-title cgt-overline-md">
            {translate('search_page.filters.search_within.related_searches')}
          </span>
          <div className="search-within__related-tags" role="group" aria-labelledby="search-within-related-title">
            {showDraft && (
              <Button
                type="secondary"
                size="tiny"
                actionType="button"
                onClick={onAddDraft}
                customClass="search-within__related-tag"
              >
                {draftTerm}
              </Button>
            )}
            {relatedTags.map(tag => (
              <Button
                key={tag}
                type="secondary"
                size="tiny"
                actionType="button"
                onClick={() => handleAdd(tag)}
                customClass="search-within__related-tag"
              >
                {tag}
              </Button>
            ))}
          </div>
        </div>
      )}
      <div className="search-within__actions">
        <Button
          type="primary"
          size="small"
          actionType="button"
          customClass="search-within__search"
          onClick={onSearch}
        >
          {translate('search_page.filters.search_within.search_with_count', { total: formattedTotal })}
        </Button>
        <Button
          type="ghost-primary"
          size="small"
          actionType="button"
          customClass="filter-wrapper__reset-button filter-wrapper__reset-button--inline search-within__reset"
          onClick={onReset}
          disabled={isResetDisabled}
        >
          {translate('search_page.filters.tags.reset')}
        </Button>
      </div>
      <div className="search-within__footer">
        <Button
          type="secondary"
          actionType="button"
          customClass="search-within__footer-button"
          onClick={onReset}
          disabled={isResetDisabled}
        >
          {translate('search_page.filters.more_filters.reset')}
        </Button>
        <Button
          type="primary"
          actionType="button"
          customClass="search-within__footer-button"
          onClick={onSearch}
        >
          {translate('search_page.filters.more_filters.apply_with_count', { total: formattedTotal })}
        </Button>
      </div>
    </div>
  )
}

Panel.propTypes = {
  draft: string.isRequired,
  isStaged: bool,
  resultCount: number,
  terms: arrayOf(string).isRequired,
  tags: arrayOf(string).isRequired,
  isResetDisabled: bool.isRequired,
  onAddDraft: func.isRequired,
  onAddTag: func.isRequired,
  onRemoveTag: func.isRequired,
  onRemoveTerm: func.isRequired,
  onReset: func.isRequired,
  onSearch: func.isRequired,
  onTermChange: func.isRequired,
}

export default memo(Panel)
